import { differenceInCalendarDays, endOfMonth, max, min, startOfMonth } from 'date-fns'
import { createDateWithoutTimezone } from '@omnea-app/packages/universal/date-utils/createDateWithoutTimezone'
import { coarsenToFitDays, GRANULARITY_ORDER, type TimeGranularity } from './granularity'

export type DateBucketingState = {
  /** A date breakdown's default granularity: the one a time axis would take for the range. */
  rangeGranularity: TimeGranularity
  /** The finest grouping a core time breakdown on the widget's own time field can chart over the widget range. */
  rangeFloor: TimeGranularity
  /**
   * The finest grouping a date breakdown the widget range does not bound can chart — a date question,
   * keyed by its value-date member, or a core time breakdown on another member than the widget's time
   * field, keyed by that member; null until its span is measured. A core time breakdown on the widget's
   * own time field has no entry.
   */
  spanFloorByMember: Record<string, TimeGranularity | null>
}

/** The first and last calendar months a date's values fall in, each given by any day within it. */
export type MonthSpan = { first: Date; last: Date }

export type ResolvedDateBucket = {
  /** Where the dotted `member.granularity` dimension sits among the query's `dimensions`. */
  dimensionIndex: number
  /** The undotted time member, which a drill-down bounds with inDateRange. */
  rawMember: string
  granularity: TimeGranularity
}

export type DateGrouping = {
  /** What the query groups by. */
  effective: TimeGranularity
  /** The finest grouping the range or the answers' span can chart; null while a probed span is unmeasured. */
  legibleFloor: TimeGranularity | null
  /** One step coarser than an earlier applied date breakdown's grouping, so the two don't multiply into a grid of cells. */
  siblingFloor: TimeGranularity | null
}

const isFinerGranularity = (candidate: TimeGranularity, than: TimeGranularity): boolean =>
  GRANULARITY_ORDER.indexOf(candidate) < GRANULARITY_ORDER.indexOf(than)

const coarserOf = (granularity: TimeGranularity, floor: TimeGranularity | null): TimeGranularity =>
  floor != null && isFinerGranularity(granularity, floor) ? floor : granularity

type DateGroupingFloors = Pick<DateGrouping, 'siblingFloor' | 'legibleFloor'>

const clampToFloors = (granularity: TimeGranularity, { legibleFloor, siblingFloor }: DateGroupingFloors): TimeGranularity =>
  coarserOf(coarserOf(granularity, legibleFloor), siblingFloor)

/** The groupings no floor rules out, finest first. The effective grouping is always among them. */
export const selectableGranularities = (floors: DateGroupingFloors): TimeGranularity[] =>
  GRANULARITY_ORDER.filter((granularity) => clampToFloors(granularity, floors) === granularity)

// A saved pick is coarsened here rather than rewritten in the config, so a range that can't chart it
// (a dashboard's override among them) leaves it intact for when one can. The unsaved default is clamped
// the same way: it is the range default, which an earlier date breakdown's sibling floor can sit above.
export const resolveDateGrouping = ({
  saved,
  auto,
  ...floors
}: DateGroupingFloors & { saved: TimeGranularity | null; auto: TimeGranularity }): DateGrouping => ({
  effective: clampToFloors(saved ?? auto, floors),
  ...floors,
})

/** The next grouping up, capped at year. */
export const oneStepCoarser = (granularity: TimeGranularity): TimeGranularity =>
  GRANULARITY_ORDER[GRANULARITY_ORDER.indexOf(granularity) + 1] ?? granularity

// The widget range bounds only its own time field, not a date question's answers or another core
// time member, so their bucket count comes from their own span. That span is only known to the month,
// so it runs from the first month's first day to the last month's last day: a span measured shorter
// would let a grouping through that draws more buckets than the chart can take.
export const coarsenToFitSpan = (granularity: TimeGranularity, span: MonthSpan): TimeGranularity =>
  coarsenToFitDays(granularity, differenceInCalendarDays(endOfMonth(span.last), startOfMonth(span.first)) + 1)

/** One date member's probe: its rows each carry one month in `_rawLabel`; `settled` is loaded without error. */
export type MonthProbe = { member: string; rows: readonly { _rawLabel: string | null }[]; settled: boolean }

// A member with no probe, or an unsettled one, maps to null (see spanAutoGranularity); a settled probe
// with no dated rows has no entry. No entry means unconstrained, so an unprobed member must never lack one.
export const probedMonthSpans = (members: readonly string[], probes: readonly MonthProbe[]): Record<string, MonthSpan | null> => {
  const probeByMember = new Map(probes.map((probe) => [probe.member, probe]))
  const spans: Record<string, MonthSpan | null> = {}
  for (const member of members) {
    const probe = probeByMember.get(member)
    if (!probe?.settled) {
      spans[member] = null
      continue
    }
    const months = probe.rows.flatMap((row) => (row._rawLabel == null ? [] : [createDateWithoutTimezone(row._rawLabel)]))
    if (months.length > 0) spans[member] = { first: min(months), last: max(months) }
  }
  return spans
}

export const dateBucketingState = ({
  rangeGranularity,
  rangeFloor,
  spans,
}: Pick<DateBucketingState, 'rangeGranularity' | 'rangeFloor'> & {
  spans: Record<string, MonthSpan | null>
}): DateBucketingState => {
  const spanFloorByMember = Object.fromEntries(
    Object.entries(spans).map(([member, span]) => [member, span == null ? null : coarsenToFitSpan('day', span)])
  )
  return { rangeGranularity, rangeFloor, spanFloorByMember }
}

// Bar count only falls as the grouping coarsens, so the coarser of the start and the span floor is
// the first grouping from the start that fits the span. A member with no probe entry keeps the start.
// A null floor is a span not yet measured (or whose probe failed). It groups by year, like a numeric
// breakdown with no entry falls to its coarsest width: an unmeasured span can be decades wide, and
// the finer grouping would fetch hundreds of buckets only to be replaced once the probe settles.
export const spanAutoGranularity = (dates: DateBucketingState, member: string): TimeGranularity => {
  const start = dates.rangeGranularity
  if (!(member in dates.spanFloorByMember)) return start
  const spanFloor = dates.spanFloorByMember[member]
  return spanFloor == null ? 'year' : coarserOf(start, spanFloor)
}
